// Theme orchestrator – mirrors the structure of qem-web-app/app/themes/createTheme.ts.
// Each concern lives in its own file; this file only assembles the final theme object.

import { createTheme } from '@mui/material/styles';
import type { Palette } from '@mui/material/styles';
import { applyOpacityToHex, replaceRgbaColor } from 'themes/themeUtils';
import { createCachedSelector, LruObjectCache } from 're-reselect';
import createPalette from 'themes/createPalette';
import createTypography from 'themes/createTypography';
import createComponents from 'themes/createComponents';
import createMixins from 'themes/createMixins';

type ThemeMode = 'light' | 'dark';

// ---- Utility (re-exported for external consumers) ----

export const boxShadowsToDropShadows = (boxShadows: string): string => {
  const boxShadowsMatches = boxShadows.matchAll?.(/(.+?(?:\([^)]*\))*?)(?:,|$)/g);
  const dropShadows = boxShadowsMatches && Array.from(boxShadowsMatches, (match) => match.length ? match[1] : undefined)
    .filter((boxShadow): boxShadow is string => !!boxShadow?.includes(' '))
    .map((boxShadow) => {
      const boxShadowParamsMatches = boxShadow.matchAll(/(\S+?(?:\(.*?\))?)(?:\s|$)+/g);
      const boxShadowParams = Array.from(boxShadowParamsMatches, (match) => match.length ? match[1] : undefined);
      return `drop-shadow(${
        boxShadowParams
          .filter((param) => param !== 'inset')
          .filter((param, index) => index !== 3 || !/^(\+|-)?\d+?.*?$/m.test(param ?? ''))
          .join(' ')
      })`;
    })
    .join(' ');
  return dropShadows || boxShadows;
};

// ---- Shape helpers ----

const steps = 20;
const singleStep = 100 / steps;
const halfStep = singleStep / 2;
const bottomCutOffPath = [...Array(steps + 1).keys()]
  .slice(1, steps + 1)
  .reverse()
  .map((step) => `${step * singleStep}% ${100}%, ${step * singleStep - halfStep}% ${100 - halfStep}%, ${step * singleStep - halfStep - halfStep}% ${100}%`)
  .join(', ');
const bottomCutOffClipPath = `polygon(0 0, 100% 0, 100% 100%, ${bottomCutOffPath}, 0 100%)`;

const createThemeOverrides = (mode: ThemeMode) => {
  const paletteOptions = createPalette(mode);
  // Both palette files fully define all fields — safe to treat as resolved Palette
  // for internal factory functions that need required (non-optional) color access.
  const palette = paletteOptions as unknown as Palette;

  return {
    palette: paletteOptions,
    typography: createTypography(palette),
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 768,   // inserted, default was 960, added xxl
        lg: 960,
        xl: 1280,
        xxl: 1920,
      },
    },
    spacing: 8,

    mixins: createMixins(palette),

    shape: {
      padding: [0, 4, 8, 12, 16, 20, 24, 32, 40, 48, 56, 64, 70, 78, 86, 94, 102],
      shapesRadius: {
        rounded: '50%',
      },
      bottomCutOffClipPath,
    },

    defaults: {
      dense: window.screen.width > 1024,
      content: {
        height: '100%',
        width: 960,
        pageMinWidth: 690,
        paddingLeft: 32,
        paddingLeftMobile: 32,
        paddingRight: 32,
        paddingRightMobile: 32,
      },
      headers: {
        subHeaderHeight: 54,
        subHeaderHeightTall: 70,
        headerHeight: 70,
      },
    },
    components: createComponents(palette),

    applyOpacityToHex,
    replaceRgbaColor,
    project: 'Quicken',
  };
};

// ---- Selector ----

// don't use this method directly ever
export const getThemeByKey = createCachedSelector(
  (themeKey: string | null | undefined) => themeKey || 'light',
  (themeKey: string) => {
    const themeOverrides = createThemeOverrides(themeKey as ThemeMode);
    return createTheme(themeOverrides);
  },
)({
  keySelector: (themeKey: string | null | undefined) => themeKey || '',
  cacheObject: new LruObjectCache({ cacheSize: 5 }),
});
